import {
    Broadcast,
    CreatorProductEntitlementsBindingModelDiscriminator,
    LiveInputResult,
    SimulcastSwitcherStreamSetting
} from "@switcherstudio/switcher-api-client";
import { trackEvent } from "helpers/analyticsHelpers";
import rollbar from "helpers/rollbar";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useThumbnailUpload } from "hooks/useThumbnailUpload";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import {
    DestinationDescriptor,
    YouTubePrivacyOptions
} from "views/page-content/platforms/platform/simulcast/types";
import { PlatformId } from "views/page-content/platforms/types";
import CheckmarkIcon from "assets/icons/checkmark-filled.svg?react";
import ErrorIcon from "assets/icons/exclamation.svg?react";
import styles from "./index.module.scss";
import { useClaimCheck } from "hooks/useClaimCheck";
import {
    FacebookForm,
    FacebookProfile,
    TwitchForm,
    YouTubeForm
} from "store/platforms/types";
import {
    createFacebookStream,
    updateFacebookStream,
    createYouTubeEvent,
    updateYoutubeEvent,
    updateTwitchChannel,
    updateVideoPlayerStream
} from "store/platforms/thunks";
import { AppDispatch } from "store/store";
import { switcherSdk } from "utils/switcher-sdk";
import { SubmissionStatus } from "../../types";
import {
    setSelectedYoutubeChannel,
    updateFacebookForm,
    updateTwitchForm,
    updateYoutubeForm
} from "store/platforms/slice";
import { ProcessingDestination } from "./ProcessingDestination";
import { motion } from "framer-motion";

interface ProcessingStepProps {
    isSubmitting: boolean;
    setIsSubmitting: (isSubmitting: boolean) => void;
    setSubmissionStatus: (status: SubmissionStatus) => void;
}

export enum DestinationProcessingStatus {
    Processing = "processing",
    Success = "success",
    Error = "error"
}

export const ProcessingStep = ({
    isSubmitting,
    setIsSubmitting,
    setSubmissionStatus
}: ProcessingStepProps) => {
    const hasGatedContentAccess = useClaimCheck("gatedcontent");
    const { general, videoplayer } = useSelector(
        (state: RootState) => state.platforms
    );
    const { generalForm, selectedStreams } = general;
    const destinations = useMemo(
        () => generalForm?.selectedSimulcastDestinations ?? [],
        [generalForm?.selectedSimulcastDestinations]
    );
    const { ProjectId } = useSelector(
        (state: RootState) => state.user?.userInfo?.MetaData?.Details
    );

    const dispatch = useDispatch<AppDispatch>();

    const { handleUploadThumbnail } = useThumbnailUpload(
        generalForm.scheduledImageFile,
        generalForm.scheduledImageFileUrl
    );

    const handleThumbnailUpload = useCallback(async () => {
        if (!generalForm?.scheduledImageFile) return;
        try {
            return (await handleUploadThumbnail())?.Id;
        } catch (error) {
            rollbar.error("Thumbnail upload failed", error);
            throw new Error("Thumbnail upload failed");
        }
    }, [generalForm?.scheduledImageFile, handleUploadThumbnail]);

    const { dispatchApiRequest: createBroadcast } = useSwitcherClient(
        (client) => client.broadcasts_PostBroadcast
    );

    const handleCreateBroadcast = useCallback(
        async (thumbnailAssetId: string) => {
            const broadcast: Broadcast = {
                Title: generalForm.title,
                Description: generalForm.description,
                ProjectId,
                EnableRecording: true,
                ThumbnailAssetId: thumbnailAssetId,
                StartsAt: generalForm.scheduledStartTime.toISOString(),
                EnableSwitcherPlayer:
                    generalForm.selectedSimulcastDestinations.some(
                        (dest) => dest.platform === PlatformId.VideoPlayer
                    ),
                EnableLiveShopping:
                    (videoplayer?.videoPlayerForm
                        ?.enableLiveShopping as boolean) ?? false
            };

            try {
                const res = await createBroadcast([broadcast]);

                trackEvent("Event Created", {
                    platform: "Simulcast",
                    inDashboard: true,
                    broadcastId: res.Id,
                    name: generalForm.title,
                    scheduledForLater: true,
                    scheduledTime: generalForm.scheduledStartTime.toISOString(),
                    scheduledTimeFromNow: Math.floor(
                        (generalForm.scheduledStartTime.getTime() -
                            new Date().getTime()) /
                            1000
                    ),
                    existingScheduled:
                        selectedStreams?.facebook !== null ||
                        selectedStreams?.youtube !== null,
                    coverImageProvided: !!generalForm.scheduledImageFile,
                    collectionsSpecified:
                        videoplayer?.videoPlayerForm?.playlistIds?.length > 0
                });

                return res;
            } catch (error) {
                rollbar.error("Broadcast creation failed", error);
                throw new Error("Broadcast creation failed");
            }
        },
        [generalForm, ProjectId, videoplayer, createBroadcast, selectedStreams]
    );

    const { dispatchApiRequest: getStreamSetting } = useSwitcherClient(
        (client) => client.switcherStreamSettings_GetSwitcherStreamSetting
    );

    const { dispatchApiRequest: updateStreamSetting } = useSwitcherClient(
        (client) => client.switcherStreamSettings_PostSwitcherStreamSetting
    );

    const { dispatchApiRequest: createOutput } = useSwitcherClient(
        (client) => client.broadcasts_CreateOutput
    );

    const [postStatuses, setPostStatuses] = useState<
        Record<string, DestinationProcessingStatus>
    >({});

    const [postErrors, setPostErrors] = useState<Record<string, string>>({});

    const finalizeFormDetailsInState = useCallback(
        (destinations: DestinationDescriptor[]) => {
            for (const d of destinations) {
                const { id: destId, platform } = d;

                switch (platform) {
                    case PlatformId.Youtube: {
                        // Make selectedPrivacy the correct type (fallback to default if no selection)
                        const selectedPrivacy = d?.platformSpecificSettings
                            ?.selectedPrivacy
                            ? {
                                  name: d?.platformSpecificSettings?.selectedPrivacy.toString(),
                                  value: d?.platformSpecificSettings?.selectedPrivacy.toLowerCase()
                              }
                            : {
                                  name: YouTubePrivacyOptions.Public.toString(),
                                  value: YouTubePrivacyOptions.Public.toLowerCase()
                              };
                        const form: YouTubeForm = {
                            isForKids:
                                d?.platformSpecificSettings?.isForKids ?? false,
                            allowEmbedding:
                                d?.platformSpecificSettings?.allowEmbedding ??
                                false,
                            selectedPrivacy
                        };
                        dispatch(updateYoutubeForm(form));
                        dispatch(
                            setSelectedYoutubeChannel(
                                d?.platformSpecificSettings?.channel
                            )
                        );
                        break;
                    }
                    case PlatformId.Facebook: {
                        const form: FacebookForm = {
                            selectedTags:
                                d?.platformSpecificSettings?.contentTags ?? [],
                            enableBrandedContent:
                                !!d?.platformSpecificSettings
                                    ?.brandedContentSelection,
                            brandedContentSelection:
                                d?.platformSpecificSettings
                                    ?.brandedContentSelection ?? "",
                            enableCrossposting:
                                d?.platformSpecificSettings?.enableCrossposting,
                            selectedCrosspostingOptions:
                                d?.platformSpecificSettings?.crosspostOptions ??
                                [],
                            enableModeration:
                                d.platformSpecificSettings
                                    ?.commentModerationOptions?.length > 0,
                            selectedCommentModerationOptions:
                                d?.platformSpecificSettings
                                    ?.commentModerationOptions ?? [],
                            privacy: d?.platformSpecificSettings
                                ?.selectedVisibility || {
                                value: "EVERYONE",
                                name: "Everyone"
                            }
                        };
                        dispatch(updateFacebookForm({ [destId]: form }));
                        break;
                    }
                    case PlatformId.Twitch: {
                        const selectedGame = d?.platformSpecificSettings
                            ?.category
                            ? d?.platformSpecificSettings?.category?.id.toString()
                            : null;
                        const form: TwitchForm = {
                            delay: d?.platformSpecificSettings?.delay ?? 0,
                            selectedGame,
                            ingestServer:
                                d?.platformSpecificSettings?.ingestServer
                        };
                        dispatch(updateTwitchForm(form));
                        break;
                    }
                    case PlatformId.VideoPlayer:
                    case PlatformId.AmazonLive:
                    case PlatformId.Caast:
                    case PlatformId.Instafeed:
                    case PlatformId.MsStream:
                    case PlatformId.Rtmp:
                    default: {
                        // Ignore collections and Custom RTMP channels
                        break;
                    }
                }
            }
        },
        [dispatch]
    );

    const createPosts = useCallback(
        async (broadcast: Broadcast) => {
            const destinations = generalForm?.selectedSimulcastDestinations;
            if (!destinations?.length) return;

            finalizeFormDetailsInState(destinations);

            const requests: Promise<void>[] = [];

            // small helpers for status + errors
            const setStatus = (
                id: string,
                status: DestinationProcessingStatus
            ) => setPostStatuses((prev) => ({ ...prev, [id]: status }));

            const clearError = (id: string) =>
                setPostErrors((prev) => ({ ...prev, [id]: undefined }));

            const setErrorMsg = (id: string, message: string) =>
                setPostErrors((prev) => ({ ...prev, [id]: message }));

            // run a unit of work with consistent status/error handling
            const withStatus = (id: string, work: () => Promise<void>) => {
                setStatus(id, DestinationProcessingStatus.Processing);
                const promise = (async () => {
                    try {
                        await work();
                        clearError(id);
                        setStatus(id, DestinationProcessingStatus.Success);
                    } catch (err: any) {
                        setErrorMsg(
                            id,
                            err?.message ?? String(err) ?? "Unknown error"
                        );
                        setStatus(id, DestinationProcessingStatus.Error);
                        throw err;
                    }
                })();
                return promise;
            };

            for (const d of destinations) {
                const { id: destId, platform } = d;

                switch (platform) {
                    case PlatformId.Youtube: {
                        requests.push(
                            withStatus(destId, async () => {
                                const action =
                                    general.selectedStreams.youtube === null
                                        ? createYouTubeEvent({
                                              showNotification: false,
                                              swiBroadcastId: broadcast.Id,
                                              isSimulcast: true
                                          })
                                        : updateYoutubeEvent({
                                              swiBroadcastId: broadcast.Id,
                                              isSimulcast: true
                                          });

                                const res = await dispatch(action);
                                await createOutput([
                                    broadcast.Id,
                                    (res as any).payload
                                ]);
                            })
                        );
                        break;
                    }
                    case PlatformId.Facebook: {
                        requests.push(
                            withStatus(destId, async () => {
                                const action =
                                    general.selectedStreams.facebook === null
                                        ? createFacebookStream({
                                              broadcastId: broadcast.Id,
                                              selectedDest: {
                                                  id: d.id,
                                                  edge: d
                                                      .platformSpecificSettings
                                                      ?.edge,
                                                  name: d.name,
                                                  picture: {
                                                      data: { url: null }
                                                  }
                                              } as FacebookProfile,
                                              showNotification: false,
                                              isSimulcast: true
                                          })
                                        : updateFacebookStream({
                                              selectedDest: {
                                                  id: d.id,
                                                  edge: d.type,
                                                  name: d.name
                                              } as FacebookProfile,
                                              isSimulcast: true
                                          });

                                const res = await dispatch(action);
                                await createOutput([
                                    broadcast.Id,
                                    (res as any).payload
                                ]);
                            })
                        );
                        break;
                    }
                    case PlatformId.Twitch: {
                        requests.push(
                            withStatus(destId, async () => {
                                const res = await dispatch(
                                    updateTwitchChannel({
                                        broadcastId: broadcast.Id,
                                        isSimulcast: true,
                                        showNotification: false
                                    })
                                );
                                await createOutput([
                                    broadcast.Id,
                                    (res as any).payload
                                ]);
                            })
                        );
                        break;
                    }
                    case PlatformId.VideoPlayer: {
                        requests.push(
                            withStatus(destId, async () => {
                                await dispatch(
                                    updateVideoPlayerStream({
                                        broadcast,
                                        isSimulcast: true,
                                        showNotification: false
                                    })
                                );
                            })
                        );
                        break;
                    }

                    case PlatformId.AmazonLive:
                    case PlatformId.Caast:
                    case PlatformId.Instafeed:
                    case PlatformId.MsStream:
                    case PlatformId.Rtmp: {
                        requests.push(
                            withStatus(destId, async () => {
                                const existing = await getStreamSetting([d.id]);

                                // sanitize + update
                                const {
                                    ["switcher-stream-setting-id"]: _omit,
                                    ...clean
                                } = existing ?? {};
                                clean.broadcastId = broadcast.Id;
                                clean["is-direct-to-provider"] = false;
                                clean["user-editable"] = false;

                                const res = await updateStreamSetting([clean]);
                                await createOutput([broadcast.Id, res]);
                            })
                        );
                        break;
                    }

                    default: {
                        // You can decide to mark this as Error in UI as well, but throwing is fine.
                        requests.push(
                            withStatus(destId, async () => {
                                throw new Error(
                                    `Unsupported platform: ${platform}`
                                );
                            })
                        );
                        break;
                    }
                }
            }

            const results = await Promise.allSettled(requests);
            return results.every((result) => result.status === "fulfilled");
        },
        [
            generalForm?.selectedSimulcastDestinations,
            general?.selectedStreams,
            dispatch,
            createOutput,
            getStreamSetting,
            updateStreamSetting,
            setPostStatuses,
            setPostErrors,
            finalizeFormDetailsInState
        ]
    );

    const { dispatchApiRequest: postPlaylistBroadcast } = useSwitcherClient(
        (client) =>
            client.videoPlayerPlaylist_PostVideoPlayerPlaylistBroadcastById
    );

    const { dispatchApiRequest: createEntitlements } = useSwitcherClient(
        (client) => client.creatorProductEntitlements_Create
    );

    const handleAddToCollections = useCallback(
        async (broadcastId: string) => {
            const collectionIds =
                videoplayer?.videoPlayerForm?.playlistIds?.filter(
                    (id): id is string => !!id
                );

            if (!collectionIds || collectionIds.length === 0) return;

            const productId = videoplayer?.videoPlayerForm?.creatorProductId;

            // One task per playlist, with contextualized try/catch inside.
            const tasks = collectionIds.map(async (playlistId) => {
                try {
                    // 1) Create/attach the broadcast to the playlist
                    const playlistBroadcastResponse =
                        await postPlaylistBroadcast([playlistId, broadcastId]);

                    const playlistBroadcastId =
                        playlistBroadcastResponse?.PlaylistBroadcast?.Id;

                    if (!playlistBroadcastId) {
                        throw new Error(
                            `Missing PlaylistBroadcast.Id for playlist ${playlistId}`
                        );
                    }

                    // 2) If gated, create entitlements
                    if (hasGatedContentAccess && productId) {
                        await createEntitlements([
                            {
                                ProductEntitlements: [
                                    {
                                        ProductId: productId,
                                        VideoPlayerPlaylistBroadcastId:
                                            playlistBroadcastId,
                                        Discriminator:
                                            CreatorProductEntitlementsBindingModelDiscriminator._2
                                    }
                                ]
                            }
                        ]);
                    }
                } catch (err) {
                    // Add rich context, then rethrow so Promise.allSettled can capture it.
                    const message =
                        err instanceof Error
                            ? err.message
                            : String(err ?? "Unknown error");
                    throw new Error(
                        `Failed to add broadcast ${broadcastId} to playlist ${playlistId}: ${message}`,
                        { cause: err instanceof Error ? err : undefined }
                    );
                }
            });

            // Run all tasks; collect any failures; throw once with details.
            const results = await Promise.allSettled(tasks);
            const errors = results
                .filter(
                    (r): r is PromiseRejectedResult => r.status === "rejected"
                )
                .map((r) => r.reason);

            return errors.length === 0;
        },
        [
            videoplayer,
            postPlaylistBroadcast,
            createEntitlements,
            hasGatedContentAccess
        ]
    );

    const { dispatchApiRequest: createInput } = useSwitcherClient(
        (client) => client.broadcasts_CreateInput
    );

    const { dispatchApiRequest: initialize } = useSwitcherClient(
        (client) => client.broadcasts_InitializeBroadcast
    );

    const initializeBroadcast = useCallback(
        async (broadcastId: string, input: LiveInputResult) => {
            const setting: SimulcastSwitcherStreamSetting = {
                ...generalForm.selectedQuality.setting
            };
            setting["channel-name"] = generalForm.title;
            const streamSetting = await initialize([
                broadcastId,
                { input, setting }
            ]);

            return streamSetting;
        },
        [generalForm, initialize]
    );

    const { dispatchApiRequest: deleteBroadcast } = useSwitcherClient(
        (client) => client.broadcasts_DeleteBroadcast
    );

    const createSimulcastBroadcast = useCallback(async () => {
        let streamSetting: SimulcastSwitcherStreamSetting | undefined;
        try {
            const thumbnailAssetId = await handleThumbnailUpload();
            const broadcast = await handleCreateBroadcast(thumbnailAssetId);

            try {
                const input = await createInput([broadcast.Id]);
                const postsResultsSuccess = await createPosts(broadcast);
                const collectionsResultsSuccess = await handleAddToCollections(
                    broadcast.Id
                );
                streamSetting = await initializeBroadcast(broadcast.Id, input);

                if (postsResultsSuccess && collectionsResultsSuccess) {
                    setSubmissionStatus(SubmissionStatus.Success);
                } else if (!postsResultsSuccess && !collectionsResultsSuccess) {
                    throw new Error(
                        "Failed to create posts and add to collections"
                    );
                } else {
                    setSubmissionStatus(SubmissionStatus.Mixed);
                }
            } catch (e) {
                await deleteBroadcast([broadcast.Id]);
                throw e;
            } finally {
                if (
                    switcherSdk.clientId !== "Generic" &&
                    switcherSdk.api.versionMajor >= 2
                ) {
                    return switcherSdk.terminateBCProfileCreation(
                        streamSetting
                    );
                }
            }
        } catch (e) {
            // TODO: Better handle errors
            console.error(`ERROR: ${e.message}`);
            setSubmissionStatus(SubmissionStatus.Failure);
        } finally {
            setIsSubmitting(false);
        }
    }, [
        handleThumbnailUpload,
        setIsSubmitting,
        handleCreateBroadcast,
        handleAddToCollections,
        createInput,
        createPosts,
        initializeBroadcast,
        deleteBroadcast,
        setSubmissionStatus
    ]);

    useEffect(() => {
        createSimulcastBroadcast();
    }, [createSimulcastBroadcast]);

    const { allSuccessful, allWrong } = useMemo(() => {
        const allStatuses = destinations.map((destination) => {
            return (
                postStatuses[destination.id] ||
                DestinationProcessingStatus.Processing
            );
        });

        return {
            allSuccessful: allStatuses.every(
                (status) => status === DestinationProcessingStatus.Success
            ),
            allWrong: allStatuses.every(
                (status) => status === DestinationProcessingStatus.Error
            )
        };
    }, [destinations, postStatuses]);

    const headerContent = useMemo(() => {
        if (isSubmitting) {
            return (
                <>
                    <h6>Connecting...</h6>
                    <p>
                        Please standby as we configure your selected
                        destinations. See details below.
                    </p>
                </>
            );
        }

        if (allSuccessful) {
            return (
                <>
                    <CheckmarkIcon className={styles.checkmark} />
                    <h6>Your Livestream is Scheduled</h6>
                    <p>All destinations successfully configured.</p>
                </>
            );
        }

        if (allWrong) {
            return (
                <>
                    <ErrorIcon className={styles.exclamation} />
                    <h6>Something Went Wrong</h6>
                    <p>An error occurred when scheduling your livestream.</p>
                </>
            );
        }

        if (!allSuccessful && !allWrong) {
            return (
                <>
                    <h6>Attention</h6>
                    <p>
                        Some destinations could not be properly configured. See
                        details below.
                    </p>
                </>
            );
        }
    }, [isSubmitting, allSuccessful, allWrong]);

    const orderedDestinations = useMemo(() => {
        return [...destinations].sort((a, b) => {
            // Sort by status: Success > Failure > Processing
            const statusOrder = {
                [DestinationProcessingStatus.Success]: 0,
                [DestinationProcessingStatus.Error]: 1,
                [DestinationProcessingStatus.Processing]: 2
            };
            const statusA =
                postStatuses[a.id] || DestinationProcessingStatus.Processing;
            const statusB =
                postStatuses[b.id] || DestinationProcessingStatus.Processing;

            // Then by name alphabetically
            if (statusA === statusB) {
                return a.name.localeCompare(b.name);
            }

            return statusOrder[statusA] - statusOrder[statusB];
        });
    }, [destinations, postStatuses]);

    return (
        <section className={styles["processing-step"]}>
            <header>{headerContent}</header>

            {orderedDestinations.length > 0 && (
                <motion.ul
                    className={styles["destination-list"]}
                    layout
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                >
                    {orderedDestinations.map((destination) => (
                        <motion.li
                            key={destination.id}
                            layout
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                        >
                            <ProcessingDestination
                                destination={destination}
                                status={
                                    postStatuses[destination.id] ??
                                    DestinationProcessingStatus.Processing
                                }
                                error={postErrors[destination.id]}
                            />
                        </motion.li>
                    ))}
                </motion.ul>
            )}
        </section>
    );
};
